import { WorkPage, WorkEmpty } from '../components/work-page.tsx';
import StaffVisitOffersFeed from '../components/staff-visit-offers-feed.tsx';
import './schedule-pages.css';
import { useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client.ts';
import { scheduleApi } from '../api/endpoints.ts';
import { useSession } from '../state/session.tsx';
import { Alert, Button, LoadingBlock } from '../components/ui.tsx';
import AddressLink from '../components/address-link.tsx';
import { VisitPhotos } from '../components/schedule/visit-photos.tsx';
import WorkOrderPage from './work-order.tsx';
import { visitInstructionsApi, instructionsKey } from '../api/visit-instructions.ts';
import { formatLocalTime, formatLongDate } from '../i18n/datetime.ts';
import type { Appointment } from '@shared/types/index.ts';
import { OPEN_APPOINTMENT_STATUSES } from '@shared/constants/index.ts';
import { AskForSwap } from '../components/shift-swap-sheet.tsx';
import TodayRoute from '../components/schedule/today-route.tsx';

export default function MyJobsPage() {
  const { t } = useTranslation();
  const { currentLocation: location, user } = useSession();
  const [date, setDate] = useState(() =>
    new Intl.DateTimeFormat('en-CA', {
      timeZone: location?.timezone ?? 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date()),
  );
  const jobs = useQuery({
    queryKey: ['crew-jobs', location?.id, user?.id, date],
    queryFn: () =>
      api.get<{ items: Appointment[]; truncated: boolean }>(`/schedule/appointments?from=${date}&to=${date}`),
  });
  return (
    <WorkPage title={t('crew.title')} description={t('crew.intro')}>
      <OwnOpenPunches />
      <TodayRoute compact />
      <label className="field-label" htmlFor="crew-date">
        {t('crew.date')}
      </label>
      <input
        id="crew-date"
        className="field-control mb-5 max-w-xs"
        type="date"
        value={date}
        onChange={(e) => {
          if (e.target.value) setDate(e.target.value);
        }}
      />
      {jobs.isLoading && <LoadingBlock label={t('crew.loading')} />}
      {jobs.isError && <Alert tone="error">{jobs.error.message}</Alert>}
      {jobs.data?.truncated && <Alert>{t('crew.truncated')}</Alert>}
      {jobs.data?.items.length === 0 && <WorkEmpty title={t('crew.empty')}>{t('crew.emptyDetail')}</WorkEmpty>}
      <ul className="grid gap-4 sm:grid-cols-2">
        {jobs.data?.items.map((job) => (
          <li key={job.id} className="card flex flex-col gap-2">
            <p className="text-sm font-semibold">{formatLocalTime(job.startTimeLocal)}</p>
            <h2 className="text-lg font-bold">{job.customerName}</h2>
            <p>{job.propertyAddress}</p>
            <p className="text-sm">
              {t(`schedule.status.${job.status}`, { defaultValue: job.status.replaceAll('_', ' ') })}
            </p>
            <Link className="btn btn-primary mt-auto self-start" to={`/my-jobs/${job.id}`}>
              {t('crew.open')}
            </Link>
          </li>
        ))}
      </ul>
      <StaffVisitOffersFeed />
    </WorkPage>
  );
}

export function CrewJobPage() {
  const { t } = useTranslation();
  const { appointmentId = '' } = useParams();
  const { currentLocation: location, user, company, impersonatedBy } = useSession();
  const cache = useQueryClient();
  const key = ['appointment', appointmentId];
  const job = useQuery({ queryKey: key, queryFn: () => scheduleApi.appointment(appointmentId) });
  const instructions = useQuery({
    queryKey: instructionsKey(appointmentId, user, company, location?.id, impersonatedBy),
    queryFn: () => visitInstructionsApi.read(appointmentId),
    refetchInterval: 30_000,
  });
  const attempt = useRef<string | null>(null);
  const clock = useMutation({
    mutationFn: async () => {
      const clockedIn = job.data?.myClockState === 'in';
      attempt.current ??= crypto.randomUUID();
      return api.post(
        `/schedule/appointments/${appointmentId}/${clockedIn ? 'clock-out' : 'clock-in'}`,
        clockedIn ? {} : { clientEntryId: attempt.current },
      );
    },
    onSuccess: async () => {
      attempt.current = null;
      await cache.invalidateQueries({ queryKey: key });
    },
    onError: () =>
      cache.invalidateQueries({
        queryKey: instructionsKey(appointmentId, user, company, location?.id, impersonatedBy),
      }),
  });
  if (job.isLoading)
    return (
      <WorkPage title={t('crew.title')}>
        <LoadingBlock label={t('crew.loading')} />
      </WorkPage>
    );
  if (job.isError || !job.data)
    return (
      <div className="space-y-4">
        <Link to="/my-jobs" className="underline">
          {t('crew.back')}
        </Link>
        <Alert tone="error">{job.error?.message ?? t('crew.unavailable')}</Alert>
        <OwnOpenPunches />
      </div>
    );
  const visit = job.data;
  return (
    <div key={`${location?.id}:${user?.id}:${appointmentId}`} className="work-page crew-job-page space-y-4">
      <Link className="font-semibold underline" to="/my-jobs">
        {t('crew.back')}
      </Link>
      <section className="card">
        <h1 className="text-2xl font-bold">{visit.customerName}</h1>
        <p>
          {formatLongDate(visit.scheduledDate)} · {formatLocalTime(visit.startTimeLocal)}
        </p>
        {visit.propertyAddress && <AddressLink address={visit.propertyAddress} />}
        {company?.requireClockInToComplete && OPEN_APPOINTMENT_STATUSES.includes(visit.status) ? (
          <Alert>{t('settings.requireClockInEmployeeHint')}</Alert>
        ) : null}
        {(visit.myClockState === 'in' || !['completed', 'cancelled', 'no_show'].includes(visit.status)) && (
          <div className="mt-4">
            <p className="mb-2">{visit.myClockState === 'in' ? t('crew.clockedIn') : t('crew.ready')}</p>
            <Button
              busy={clock.isPending}
              disabled={visit.myClockState !== 'in' && !instructions.data?.acknowledged}
              onClick={() => clock.mutate()}
            >
              {visit.myClockState === 'in' ? t('crew.clockOut') : t('crew.clockIn')}
            </Button>
          </div>
        )}
        {clock.isError && <Alert tone="error">{clock.error.message}</Alert>}
        <Link to={`/chats?visitId=${appointmentId}`} className="mt-4 inline-block underline">
          {t('jobChat.open')}
        </Link>
        {OPEN_APPOINTMENT_STATUSES.includes(visit.status) &&
        visit.myClockState !== 'in' &&
        visit.startsAt > new Date().toISOString() ? (
          <AskForSwap appointmentId={appointmentId} timezone={visit.timezone} />
        ) : null}
      </section>
      <WorkOrderPage embedded />
      <section className="card">
        <VisitPhotos appointmentId={appointmentId} />
      </section>
    </div>
  );
}

export function OwnOpenPunches() {
  const { t, i18n } = useTranslation();
  const { currentLocation: location, user, can } = useSession();
  const cache = useQueryClient();
  const queryKey = ['crew-open-punches', user?.id, location?.id];
  const punches = useQuery({
    queryKey,
    enabled: !!location && can('schedule:read'),
    queryFn: () => api.get<{ items: { appointmentId: string; clockedInAt: string }[] }>('/schedule/my-open-punches'),
    refetchInterval: 30_000,
  });
  const clockOut = useMutation({
    mutationFn: (id: string) => api.post(`/schedule/appointments/${id}/clock-out`, {}),
    onSuccess: async () => {
      await Promise.all([
        cache.invalidateQueries({ queryKey: ['crew-open-punches'] }),
        cache.invalidateQueries({ queryKey: ['appointment'] }),
      ]);
    },
  });
  if (punches.isError) return <Alert tone="error">{t('crew.openPunchLoadError')}</Alert>;
  if (!punches.data?.items.length) return null;
  return (
    <section className="card mb-5" aria-label={t('crew.openPunches')}>
      <h2 className="font-semibold">{t('crew.openPunches')}</h2>
      <p>{t('crew.openPunchHelp')}</p>
      {clockOut.isError && <Alert tone="error">{clockOut.error.message}</Alert>}
      <ul className="mt-3 space-y-3">
        {punches.data.items.map((punch) => (
          <li key={punch.appointmentId} className="flex flex-wrap items-center gap-3">
            <span>
              {new Date(punch.clockedInAt).toLocaleString(i18n.language, { timeZone: location?.timezone ?? 'UTC' })}
            </span>
            <Button busy={clockOut.isPending} onClick={() => clockOut.mutate(punch.appointmentId)}>
              {t('crew.clockOut')}
            </Button>
          </li>
        ))}
      </ul>
    </section>
  );
}
